/**
 * Keeps the page's manifest pointed at the tenant the viewer is actually looking at.
 *
 * Working this out is less obvious than it sounds, because a tenant can be reached three
 * ways and only one of them is in the hostname:
 *
 *   kk.mylms.in           host  → AuthContext detects it (tenantInfo.source)
 *   learn.kkacademy.com   host  → same
 *   mylms.in/tenant/kk    PATH  → nothing in the host says "kk" at all
 *
 * The path form is the one the platform hands out, so reading only the hostname made
 * every ordinary portal install itself as "MyLMS". It lives inside the Router so a move
 * from one portal to another re-points the manifest.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { applyManifest } from '../../lib/pwa';
import { rememberedPortal } from '../../lib/session';

/** The subdomain in /tenant/<sub>/…, if that is how we got here. */
export function portalFromPath(pathname) {
  const m = /^\/tenant\/([^/?#]+)/.exec(pathname || '');
  return m ? decodeURIComponent(m[1]).toLowerCase() : null;
}

export default function ManifestLink() {
  const { tenantInfo, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const fromPath = portalFromPath(location.pathname);
    const fromHost = tenantInfo?.subdomain || null;
    // A signed-in learner on /learn/… is still inside the portal they came in by.
    const remembered = user && user.role !== 'super_admin' ? rememberedPortal() : null;

    // The PATH wins. It names a portal outright, so someone signed in to one tenant who
    // opens /tenant/<other> is looking at the other one — the host and the remembered
    // portal are both weaker evidence than the URL in front of them.
    const subdomain = fromPath || fromHost || remembered || null;

    applyManifest({
      subdomain,
      // Only meaningful when the HOST is what identified the tenant.
      customDomain: !fromPath && tenantInfo?.source === 'custom_domain',
    });
  }, [tenantInfo?.subdomain, tenantInfo?.source, location.pathname, user]);

  return null;
}
